/**
 * Per-step checkpoint upload: every workflow step saves its mesh **and** the
 * tool/session metadata that produced it, under its own server slot.
 *
 * Replaces the previous hand-written per-step blocks in `useStepNavigation`,
 * which only covered rectification / valve / locking / logo and squeezed four
 * steps into two server slots.
 *
 * Backend compatibility: the server `step` allowlist is being widened to the
 * full workflow. Until that ships, a slot the server rejects is remembered in
 * `unsupportedSlots` and the step falls back to its historical location
 * (`LEGACY_SLOT_FALLBACK`) for the rest of the session, so a frontend deploy
 * ahead of the backend keeps saving exactly what it saves today.
 */
import { exportMesh } from './exporters/modelExporter.js';
import { yieldToMain } from './yieldToMain.js';
import { EXPORT_FORMATS } from './exportConfig.js';
import {
  uploadBkDesignStepCheckpoint,
  uploadScanFinalPreviewCheckpoint,
} from '../services/erpApiService.js';
import {
  LEGACY_SLOT_FALLBACK,
  STEP_CHECKPOINT_METADATA_VERSION,
  checkpointFormatForStep,
  checkpointSlotForStep,
} from '../constants/stepCheckpointSlots.js';
import { MAIN_PROCESS_STEPS } from '../constants/flowConfig.js';
import { captureSessionState } from '../../features/step-checkpoints/sessionSnapshot.js';
import useOperationLogStore from '../../features/step-checkpoints/operationLog/operationLogStore.js';
import useUndoManifestStore from '../../features/step-checkpoints/undo/undoManifestStore.js';
import useEditorStore from '../stores/useEditorStore.js';
import useFinalizationDataStore from '../stores/useFinalizationDataStore.js';
import useLockingSystemStore from '../stores/useLockingSystemStore.js';
import {
  readLogoPlacementMetadata,
  readValveCheckpointMetadata,
  resolveLockingCheckpointVariant,
} from './bkCheckpointUpload.js';

const LOG_PREFIX = '[StepCheckpointUpload]';

/** Server slots this session has proven do not exist yet. */
const unsupportedSlots = new Set();

/** `${orderKey}:${stepId}` → fingerprint of the last successful upload. */
const lastUploadedFingerprint = new Map();

/**
 * Arrays longer than this are dropped from the metadata envelope. Per-vertex
 * buffers (ML-reduction position backups, smoothening influence weights) run to
 * hundreds of thousands of entries and would turn a small JSON blob into
 * megabytes on every step.
 */
const MAX_INLINE_ARRAY = 2048;
const MAX_DEPTH = 12;

/**
 * Deep-clone a value into something safe to JSON-encode as checkpoint metadata:
 * Sets become arrays, typed arrays and oversized arrays are replaced by a size
 * marker, functions/undefined are dropped.
 */
export function sanitizeForMetadata(value, depth = 0) {
  if (value == null) return null;
  if (depth > MAX_DEPTH) return null;

  const type = typeof value;
  if (type === 'number') return Number.isFinite(value) ? value : null;
  if (type === 'string' || type === 'boolean') return value;
  if (type === 'function' || type === 'symbol') return undefined;

  if (ArrayBuffer.isView(value)) {
    return { _omitted: 'typed_array', length: value.length };
  }
  if (value instanceof Set) return sanitizeForMetadata(Array.from(value), depth + 1);
  if (value instanceof Map) return sanitizeForMetadata(Object.fromEntries(value), depth + 1);
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) {
    if (value.length > MAX_INLINE_ARRAY) {
      return { _omitted: 'array', length: value.length };
    }
    return value.map((item) => sanitizeForMetadata(item, depth + 1) ?? null);
  }

  if (type === 'object') {
    // THREE objects and other class instances carry cyclic scene references —
    // only plain data survives.
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      const clean = sanitizeForMetadata(val, depth + 1);
      if (clean !== undefined) out[key] = clean;
    }
    return out;
  }

  return undefined;
}

/**
 * Step-specific keys kept at the top level of the metadata object, because
 * existing backend consumers and `bkResumeState` read them by name. Everything
 * else travels inside the generic `session` envelope.
 */
function legacyMetadataForStep(stepId) {
  const { appliedTools } = useFinalizationDataStore.getState();
  const clone = (v) => sanitizeForMetadata(v);

  switch (stepId) {
    case MAIN_PROCESS_STEPS.RECTIFICATION:
      return { rectification: clone(appliedTools.rectification) };
    case MAIN_PROCESS_STEPS.THICKNESS:
      // The last clinical step is the fully-modified clinical socket, so it
      // carries the rectification data too (the shape the backend already stores).
      return { rectification: clone(appliedTools.rectification) };
    case MAIN_PROCESS_STEPS.VALVE:
      return { valve: readValveCheckpointMetadata() };
    case MAIN_PROCESS_STEPS.LOCKING_MECHANISM: {
      const ls = useLockingSystemStore.getState().lockingSystem;
      return {
        locking: {
          selectedSystem: ls.selectedSystem,
          customizationEnabled: ls.customizationEnabled,
          diameter: ls.diameter,
          depth: ls.depth,
        },
      };
    }
    case MAIN_PROCESS_STEPS.LOGO_PLACEMENT:
      return { logo: readLogoPlacementMetadata() };
    default:
      return {};
  }
}

/**
 * The server rejects a metadata blob over `_CHECKPOINT_CLIENT_METADATA_MAX_BYTES`
 * (512 KiB), and a rejection loses the *whole* checkpoint, not just the oversized
 * field. Leave headroom for the JSON the backend wraps around ours.
 */
const METADATA_BUDGET_BYTES = 480 * 1024;

function jsonByteLength(value) {
  try {
    // Multi-byte characters (customer names, tool labels) cost more than one byte
    // each, so measure the encoded form rather than the string length.
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * Shrink the envelope until it fits the server's cap.
 *
 * Sheds the *newest, most reconstructible* fields first and never touches the
 * payload that was already shipping before cross-device undo existed — a step
 * whose metadata fit yesterday must still upload today. Session and applied-tools
 * drive resume and finalization, so they are never dropped; if the blob is still
 * over budget without any of the new fields, that is a pre-existing problem and
 * gets logged as an error rather than silently mangled.
 *
 * @param {Record<string, unknown>} metadata
 * @returns {Record<string, unknown>}
 */
export function fitMetadataToBudget(metadata) {
  if (jsonByteLength(metadata) <= METADATA_BUDGET_BYTES) return metadata;

  const out = { ...metadata };

  // 1. Replay parameters. Recipe undo degrades to the stored pre-apply mesh, which
  //    is exactly what the fallback chain does anyway.
  if (out.operations?.length) {
    out.operations = out.operations.map((op) => ({ ...op, params: { _omitted: 'budget' } }));
    if (jsonByteLength(out) <= METADATA_BUDGET_BYTES) {
      console.warn(`${LOG_PREFIX} metadata over budget — dropped operation params`);
      return out;
    }
  }

  // 2. The operation log itself.
  if (out.operations) {
    delete out.operations;
    if (jsonByteLength(out) <= METADATA_BUDGET_BYTES) {
      console.warn(`${LOG_PREFIX} metadata over budget — dropped the operation log`);
      return out;
    }
  }

  // 3. Per-apply undo detail, keeping the pointers and before-mesh index that
  //    cross-device undo actually needs.
  if (out.undo?.entries) {
    out.undo = {
      schemaVersion: out.undo.schemaVersion,
      stepId: out.undo.stepId,
      toolCount: out.undo.toolCount,
      beforeByTool: out.undo.beforeByTool,
      firstToolId: out.undo.firstToolId,
      lastToolId: out.undo.lastToolId,
      totalDurationMs: out.undo.totalDurationMs,
      entriesOmitted: out.undo.entries.length,
    };
    if (jsonByteLength(out) <= METADATA_BUDGET_BYTES) {
      console.warn(`${LOG_PREFIX} metadata over budget — dropped undo apply history`);
      return out;
    }
  }

  // 4. The whole undo manifest.
  if (out.undo) {
    delete out.undo;
    if (jsonByteLength(out) <= METADATA_BUDGET_BYTES) {
      console.warn(`${LOG_PREFIX} metadata over budget — dropped the undo manifest`);
      return out;
    }
  }

  console.error(
    `${LOG_PREFIX} metadata is ${jsonByteLength(out)} bytes with every optional ` +
      `field removed (budget ${METADATA_BUDGET_BYTES}) — the server may reject this ` +
      'checkpoint. Check the session snapshot for an unexpectedly large field.',
  );
  return out;
}

/**
 * Full metadata payload for a step: the legacy per-step keys, the sanitized
 * session snapshot (every tool store, the same slice used for in-session
 * restore), the operation descriptors recorded on this step, and the undo
 * manifest (apply order, timings, and where each pre-apply socket lives).
 */
export function buildStepCheckpointMetadata(stepId, { format, variant } = {}) {
  const operations = useOperationLogStore.getState().getOperationsForStep(stepId);
  return fitMetadataToBudget({
    schema_version: STEP_CHECKPOINT_METADATA_VERSION,
    checkpoint_step: stepId,
    captured_at: new Date().toISOString(),
    ...(format ? { mesh_format: format } : {}),
    ...(variant ? { variant } : {}),
    ...legacyMetadataForStep(stepId),
    applied_tools: sanitizeForMetadata(
      useFinalizationDataStore.getState().appliedTools,
    ),
    session: sanitizeForMetadata(captureSessionState()),
    operations: sanitizeForMetadata(operations),
    undo: sanitizeForMetadata(
      useUndoManifestStore.getState().serializeForStep(stepId),
    ),
  });
}

/** Cheap non-cryptographic hash so an unchanged step doesn't re-upload. */
function hashString(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/**
 * Identifies "the model + settings as they are right now". `geometryVersion`
 * bumps on every mesh mutation; vertex count and the metadata hash catch tool
 * changes that leave the geometry alone.
 */
function stepFingerprint(mesh, metadata) {
  const { geometryVersion } = useEditorStore.getState();
  const vertexCount = mesh?.geometry?.attributes?.position?.count ?? 0;
  let metaHash = 0;
  try {
    metaHash = hashString(JSON.stringify(metadata));
  } catch {
    metaHash = Date.now();
  }
  return `${geometryVersion}:${vertexCount}:${metaHash}`;
}

function checkpointFile(mesh, stepId, format) {
  const stem = `${String(stepId).toLowerCase()}_checkpoint`;
  if (format === EXPORT_FORMATS.STL) {
    const blob = exportMesh(mesh, { format: EXPORT_FORMATS.STL });
    return new File([blob], `${stem}.stl`, { type: 'application/octet-stream' });
  }
  const blob = exportMesh(mesh, { format: EXPORT_FORMATS.PLY, preserveColors: true });
  return new File([blob], `${stem}.ply`, { type: 'text/plain;charset=utf-8' });
}

/**
 * True when the server rejected the request because it doesn't know this `step`
 * slot — the signal to stop trying it and fall back for the rest of the session.
 */
function isUnknownStepError(err) {
  const status = err?.response?.status ?? err?.frappeStatusCode;
  if (status && status !== 400 && status !== 404 && status !== 422) return false;
  const text = String(err?.message ?? '').toLowerCase();
  return (
    text.includes('step') &&
    (text.includes('invalid') ||
      text.includes('unknown') ||
      text.includes('unsupported') ||
      text.includes('not allowed') ||
      text.includes('not supported'))
  );
}

/** Locking is the only slot whose variant is decided at upload time. */
function variantForStep(stepId) {
  if (stepId === MAIN_PROCESS_STEPS.LOCKING_MECHANISM) {
    return resolveLockingCheckpointVariant();
  }
  return undefined;
}

async function sendToSlot({ slot, mode, variant, file, metadata, salesOrderId, customer }) {
  if (slot === 'scan_final_preview') {
    return uploadScanFinalPreviewCheckpoint(salesOrderId, customer, file, metadata);
  }
  return uploadBkDesignStepCheckpoint({
    salesOrderId,
    customer,
    step: slot,
    mode,
    variant,
    file,
    metadata,
  });
}

/**
 * Save the model + metadata for one workflow step.
 *
 * Skips (without throwing) when the step has no checkpoint slot, no mesh is
 * loaded, or nothing changed since this step last uploaded. Network/server
 * failures throw so the caller can surface them.
 *
 * @param {string} stepId
 * @param {import('three').Mesh} mesh
 * @param {string} salesOrderId
 * @param {string} customer
 * @returns {Promise<{ ok: boolean, skipped?: boolean, reason?: string, slot?: string, result?: unknown }>}
 */
export async function uploadStepCheckpoint(stepId, mesh, salesOrderId, customer) {
  const slot = checkpointSlotForStep(stepId);
  if (!slot) return { ok: true, skipped: true, reason: 'no_slot_for_step' };
  if (!salesOrderId || !customer) return { ok: false, skipped: true, reason: 'no_order_context' };
  if (!mesh?.geometry?.attributes?.position?.count) {
    return { ok: false, skipped: true, reason: 'no_mesh' };
  }

  const format = checkpointFormatForStep(stepId);
  const variant = variantForStep(stepId);
  const metadata = buildStepCheckpointMetadata(stepId, { format, variant });

  const dedupeKey = `${salesOrderId}:${customer}:${stepId}`;
  const fingerprint = stepFingerprint(mesh, metadata);
  if (lastUploadedFingerprint.get(dedupeKey) === fingerprint) {
    return { ok: true, skipped: true, reason: 'unchanged', slot };
  }

  const fallbackSlot = LEGACY_SLOT_FALLBACK[stepId];
  if (unsupportedSlots.has(slot) && !fallbackSlot) {
    // Nowhere to put it until the backend allowlist covers this step. Bail out
    // before exporting, so we don't serialize a mesh we can't send.
    return { ok: true, skipped: true, reason: 'slot_unsupported', slot };
  }

  // Serializing a large mesh blocks the main thread; give the UI a frame first
  // so navigation to the next step still feels instant.
  await yieldToMain();
  const file = checkpointFile(mesh, stepId, format);
  await yieldToMain();

  const mode = format === EXPORT_FORMATS.STL ? 'stl' : 'ply';

  if (!unsupportedSlots.has(slot)) {
    try {
      const result = await sendToSlot({
        slot,
        mode,
        variant,
        file,
        metadata,
        salesOrderId,
        customer,
      });
      lastUploadedFingerprint.set(dedupeKey, fingerprint);
      return { ok: true, slot, result };
    } catch (err) {
      if (!fallbackSlot || !isUnknownStepError(err)) throw err;
      unsupportedSlots.add(slot);
      console.warn(
        `${LOG_PREFIX} server has no "${slot}" slot yet — falling back to "${fallbackSlot.slot}" for ${stepId}`,
      );
    }
  }

  const result = await sendToSlot({
    slot: fallbackSlot.slot,
    mode,
    variant: fallbackSlot.variant ?? variant,
    file,
    metadata: { ...metadata, checkpoint_step: fallbackSlot.tagStep ?? stepId },
    salesOrderId,
    customer,
  });
  lastUploadedFingerprint.set(dedupeKey, fingerprint);
  return { ok: true, slot: fallbackSlot.slot, result };
}
